import { useMemo } from "react";
//types
import { IHeaderFooterComm, ICouponCard, IAppliedCouponCodes } from "@swiggy-private/cart-types";
//helpers
import { isEmpty } from "@swiggy-private/js-utils";
import {
    calculateAppliedCouponsSavings,
    calculateBestCouponsSavings,
    processSavingsTemplate,
} from "@cart-client/helpers/CouponListing";

interface UseSavingsTextProps {
    appliedCouponCodes: IAppliedCouponCodes[];
    templateData?: IHeaderFooterComm;
    coupons?: ICouponCard[] | null;
    hasUserChangedCoupons?: boolean;
}

export const useSavingsText = ({
    appliedCouponCodes,
    templateData,
    coupons,
    hasUserChangedCoupons = false,
}: UseSavingsTextProps) => {
    const savingsText = useMemo(() => {
        if (isEmpty(templateData)) {
            return "";
        }

        // Choose template set based on user interaction
        const multipleCouponTemplate = hasUserChangedCoupons
            ? templateData.staticTemplateText
            : templateData.baseTemplateText;
        const singleCouponTemplate = hasUserChangedCoupons
            ? templateData.staticTemplateTextWithoutCount
            : templateData.baseTemplateTextWithoutCount;

        let calculatedSavings: number;
        let couponCount: number;

        if (isEmpty(appliedCouponCodes) && hasUserChangedCoupons) {
            const bestCouponsData = calculateBestCouponsSavings(coupons || null);
            calculatedSavings = bestCouponsData.totalSavings;
            couponCount = bestCouponsData.couponCount;
        } else {
            calculatedSavings = calculateAppliedCouponsSavings(coupons || null, appliedCouponCodes);
            couponCount = appliedCouponCodes.length;
        }

        const shouldUseSingleCouponTemplate = couponCount === 1;
        const template = shouldUseSingleCouponTemplate ? singleCouponTemplate : multipleCouponTemplate;

        if (isEmpty(template) || couponCount === 0 || calculatedSavings === 0) {
            return "";
        }

        return processSavingsTemplate(template, calculatedSavings, couponCount);
    }, [appliedCouponCodes, templateData, coupons, hasUserChangedCoupons]);

    return { savingsText };
};
